import { inject, injectable } from 'inversify'
import { Subscription } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { UserChartChanged } from '@/domain/events/UserChartChanged'
import { UserChartSynced } from '@/domain/events/UserChartSynced'
import { UserChart, UserChartPojo } from '@/domain/models/UserChart/UserChart'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'
import { DeleteUserChartsLocalDataUseCase } from '@/domain/useCases/UserCharts'

import type { IUserChartRepository } from '@/data/UserChartRepository/IUserChartRepository'
import {
  clearPendingOriginalUpload,
  clearPendingProcessedUpload,
  getAllPendingOriginalUploads,
  getAllPendingProcessedUploads,
  getPendingProcessedUpload
} from '@/data/UserChartRepository/userChartTilesOutbox'

import { IService } from '../IService'

import { I18n } from '@/i18n/I18n'
import type { IUserChartsWebClient, UserChartSyncRequest } from '@/infra/http/UserChartsWebClient/IUserChartsWebClient'
import type { ILogger } from '@/infra/logger/ILogger'
import { GlobalToastUtil } from '@/utils/globalToastUtil'

export const INITIAL_SYNC_DELAY_MS = 8000

// Bounded retry for a file upload. Only transient transport failures (NETWORK_ERROR)
// are retried; a definitive server rejection is not. The bytes also stay in the
// in-memory outbox, so anything still failing here is retried on a later sync this
// session (see _flushOrphanProcessedUploads).
export const UPLOAD_MAX_ATTEMPTS = 3
export const UPLOAD_RETRY_BASE_MS = 1000

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

@injectable()
export class UserChartSyncService implements IService {
  private inflight: Promise<Result<void>> | null = null
  private cursor: string | null = null
  private authSubscription: Subscription | null = null
  private changedSubscription: Subscription | null = null
  private initialSyncTimeout: ReturnType<typeof setTimeout> | null = null
  private lastSeenToken: string | null = null

  constructor(
    @inject(InjectionTokens.UserChartRepository) private repository: IUserChartRepository,
    @inject(InjectionTokens.UserChartsWebClient) private webClient: IUserChartsWebClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState,
    @inject(InjectionTokens.Logger) private logger: ILogger,
    @inject(InjectionTokens.DeleteUserChartsLocalDataUseCase)
    private deleteLocalDataUseCase: DeleteUserChartsLocalDataUseCase,
    @inject(InjectionTokens.I18n) private i18n: I18n
  ) {}

  async init(): Promise<Result<void>> {
    await this.repository.init()
    this.initialSyncTimeout = setTimeout(() => {
      this.initialSyncTimeout = null
      void this.sync()
    }, INITIAL_SYNC_DELAY_MS)
    return Result.ok()
  }

  start(): Result<void> {
    this.lastSeenToken = this.authState.getStateSnapshot().token
    this.authSubscription = this.authState.getState().subscribe({
      next: (snapshot) => {
        const previous = this.lastSeenToken
        const current = snapshot.token
        this.lastSeenToken = current
        if (previous && !current) {
          void this._onLogout()
        }
      }
    })
    this.changedSubscription = UserChartChanged.subscribe(() => {
      const { upserts, deletes } = this.repository.getPendingChanges()
      if (upserts.length === 0 && deletes.length === 0) return
      void this.sync()
    })
    return Result.ok()
  }

  stop(): Result<void> {
    if (this.initialSyncTimeout) {
      clearTimeout(this.initialSyncTimeout)
      this.initialSyncTimeout = null
    }
    this.authSubscription?.unsubscribe()
    this.authSubscription = null
    this.changedSubscription?.unsubscribe()
    this.changedSubscription = null
    return Result.ok()
  }

  async sync(): Promise<Result<void>> {
    if (this.inflight) return this.inflight
    this.inflight = this._runSync()
    try {
      return await this.inflight
    } finally {
      this.inflight = null
    }
  }

  private async _runSync(): Promise<Result<void>> {
    if (!this.authState.getStateSnapshot().token) {
      return Result.fail('NOT_AUTHENTICATED')
    }

    const { upserts, deletes } = this.repository.getPendingChanges()
    const request: UserChartSyncRequest = {
      since: this.cursor,
      upserts: upserts.map((chart) => chart.toPojo()),
      deletes: deletes.map((chart) => ({
        id: chart.id.toString(),
        updatedAt: chart.updatedAt ?? new Date().toISOString()
      }))
    }

    const response = await this.webClient.sync(request)
    if (response.isFailure) {
      this.logger.error('UserChartSyncService', `sync failed: ${response.error}`)
      return Result.fail(response.error)
    }

    const payload = response.getValue()

    await this._processServerUpserts(payload.upserts)
    this._processDeletes(payload.deletes.map((entry) => entry.id))

    // Metadata is on the server now; upload the processed tiles for charts
    // converted on this client before marking them synced. A chart without a
    // pending archive (e.g. a metadata-only rename, or one synced elsewhere) has
    // nothing to upload and is marked synced directly. A failed upload leaves the
    // chart pending so a later sync retries it (the archive stays in the outbox).
    const pendingIds = upserts.map((chart) => chart.id.toString())
    const processed = await this._uploadPendingProcessed(pendingIds)

    this.repository.markSynced(processed.syncedIds)
    this.repository.hardDelete(deletes.map((chart) => chart.id.toString()))

    // Flush processed uploads still in the outbox that are not among this cycle's
    // pending upserts (their metadata is already on the server). Done before
    // UserChartSynced so the boot prefetch downloads freshly-uploaded tiles.
    const orphanUploaded = await this._flushOrphanProcessedUploads(new Set(pendingIds))

    // Upload the imported originals (decoupled from the synced flag): with the source
    // on the server, a chart can be re-hydrated by re-converting it and "download
    // original" works cross-browser.
    const originalsUploaded = await this._flushPendingOriginals()

    // Tell the user (globally) once the files they just saved have actually reached
    // the server — this is the confirmation that pairs with the "uploading" modal.
    const uploadedFiles = processed.uploaded + orphanUploaded + originalsUploaded
    if (uploadedFiles > 0) {
      GlobalToastUtil.send({
        label: this.i18n.resolveToken('TOAST_USER-CHART_UPLOAD_COMPLETED'),
        leftIcon: 'Check',
        variant: 'primary'
      })
    }

    this.cursor = payload.serverNow
    UserChartSynced.next()
    return Result.ok()
  }

  // Uploads the pending processed archive for each freshly-saved chart. Returns the
  // ids to mark synced plus how many files were actually uploaded (for the toast).
  private async _uploadPendingProcessed(ids: string[]): Promise<{ syncedIds: string[]; uploaded: number }> {
    const syncedIds: string[] = []
    let uploaded = 0
    for (const id of ids) {
      const archive = getPendingProcessedUpload(id)
      if (!archive) {
        syncedIds.push(id)
        continue
      }
      const outcome = await this._uploadWithRetry(`processed ${id}`, () => this.webClient.uploadProcessed(id, archive))
      if (outcome === 'ok') {
        clearPendingProcessedUpload(id)
        syncedIds.push(id)
        uploaded += 1
      } else if (outcome === 'permanent') {
        // The server definitively rejected the upload (e.g. 4xx): retrying will not
        // help, so drop the archive to stop re-attempting it every sync. Marking the
        // chart synced lets the metadata settle (the tiles just won't be available).
        clearPendingProcessedUpload(id)
        syncedIds.push(id)
      }
      // 'transient': leave the chart pending and its ZIP in the outbox so a later sync
      // retries the upload (within this session — the outbox is in-memory).
    }
    return { syncedIds, uploaded }
  }

  // Uploads processed archives still in the outbox that are not among this sync's
  // pending upserts (their metadata is already synced). Returns how many uploaded ok.
  private async _flushOrphanProcessedUploads(excludeIds: Set<string>): Promise<number> {
    let uploaded = 0
    for (const { id, archive } of getAllPendingProcessedUploads()) {
      if (excludeIds.has(id)) continue
      const outcome = await this._uploadWithRetry(`processed ${id}`, () => this.webClient.uploadProcessed(id, archive))
      // Clear on success or on a permanent rejection. A permanent 4xx here typically
      // means the chart's metadata was never synced (e.g. a conversion abandoned
      // before Save), so its orphaned ZIP would otherwise be retried every sync.
      if (outcome === 'ok') uploaded += 1
      if (outcome === 'ok' || outcome === 'permanent') clearPendingProcessedUpload(id)
    }
    return uploaded
  }

  // Uploads every pending original in the outbox. Originals are independent of the
  // chart's `synced` flag (which tracks metadata + processed tiles), so this runs for
  // all outbox entries each sync. Returns how many uploaded ok.
  private async _flushPendingOriginals(): Promise<number> {
    let uploaded = 0
    for (const { id, blob, name } of getAllPendingOriginalUploads()) {
      const outcome = await this._uploadWithRetry(`original ${id}`, () => this.webClient.uploadOriginal(id, blob, name))
      if (outcome === 'ok') uploaded += 1
      if (outcome === 'ok' || outcome === 'permanent') clearPendingOriginalUpload(id)
    }
    return uploaded
  }

  private async _uploadWithRetry(
    label: string,
    doUpload: () => Promise<Result<void>>
  ): Promise<'ok' | 'transient' | 'permanent'> {
    for (let attempt = 0; attempt < UPLOAD_MAX_ATTEMPTS; attempt++) {
      const upload = await doUpload()
      if (upload.isSuccess) return 'ok'

      // Only transient transport failures are worth retrying; a definitive server
      // rejection (e.g. UPLOAD_FAILED) will not be fixed by trying again.
      if (upload.error !== 'NETWORK_ERROR') {
        this.logger.error('UserChartSyncService', `upload failed for ${label}: ${upload.error}`)
        return 'permanent'
      }

      if (attempt < UPLOAD_MAX_ATTEMPTS - 1) {
        await delay(UPLOAD_RETRY_BASE_MS * 2 ** attempt)
      }
    }
    this.logger.error('UserChartSyncService', `upload failed for ${label}: NETWORK_ERROR (after retries)`)
    return 'transient'
  }

  private async _processServerUpserts(upserts: UserChartPojo[]): Promise<void> {
    for (const pojo of upserts) {
      const chart = UserChart.fromPojo(pojo)
      if (!chart) continue
      await this.repository.saveChart(chart)
    }
  }

  private _processDeletes(ids: string[]): void {
    if (ids.length === 0) return
    this.repository.hardDelete(ids)
  }

  private async _onLogout(): Promise<void> {
    this.cursor = null
    const result = await this.deleteLocalDataUseCase.execute()
    if (result.isFailure) {
      this.logger.error('UserChartSyncService', `logout cleanup failed: ${result.error}`)
    }
  }
}
